import React, { useState, useEffect } from 'react';

interface Medication {
  id: number;
  medicine_name: string;
  medicine_company: string;
  medicine_composition: string;
  batch_no: string;
  expiry_date: string;
  quantity: number;
  quantity_type: string;
  tablets_per_strip: number;
  total_tablets: number;
  unit_cost: number;
  selling_price: number;
  mrp: number;
  storage_location: string;
  is_expired: boolean;
  days_to_expiry: number;
  created_at: string;
  updated_at: string;
}

interface InventoryStatsProps {
  medications: Medication[];
  searchTerm: string;
  statusFilter: string;
  sortBy: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onSortByChange: (value: string) => void;
}

// Helper function to get status text
const getStatusText = (medication: Medication) => {
  if (medication.is_expired) return 'expired';
  if (medication.quantity === 0) return 'out-of-stock';
  if (medication.days_to_expiry <= 30) return 'expiring-soon';
  if (medication.quantity < 50) return 'low-stock';
  return 'in-stock';
};

const InventoryStats: React.FC<InventoryStatsProps> = ({
  medications,
  searchTerm,
  statusFilter,
  sortBy,
  onSearchChange,
  onStatusFilterChange,
  onSortByChange
}) => {
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [statusButtonRef, setStatusButtonRef] = useState<HTMLButtonElement | null>(null);
  const [sortButtonRef, setSortButtonRef] = useState<HTMLButtonElement | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown-container]')) {
        setIsStatusDropdownOpen(false);
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{
      margin: isMobile ? '0 0.5rem 1rem 0.5rem' : '0 2rem 1rem 2rem',
      padding: isMobile ? '1rem' : '1.5rem',
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: isMobile ? '8px' : '12px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(20px)'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '0.75rem' : '1rem',
        alignItems: isMobile ? 'stretch' : 'center',
        marginBottom: '1rem'
      }}>
        {/* Search Bar */}
        <div style={{ flex: isMobile ? 'none' : '1', minWidth: isMobile ? '100%' : '300px', width: isMobile ? '100%' : 'auto' }}>
          <input
            type="text"
            placeholder="Search medications..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: '100%',
              padding: isMobile ? '0.625rem 0.875rem' : '0.75rem 1rem',
              border: '2px solid rgba(226, 232, 240, 0.8)',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.9)',
              fontSize: isMobile ? '0.875rem' : '0.9rem',
              outline: 'none',
              color: '#374151',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Status Filter and Sort By - Side by Side */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '0.75rem' : '1rem', 
          alignItems: 'stretch',
          width: isMobile ? '100%' : 'auto'
        }}>
          {/* Status Filter - Custom Dropdown */}
          <div style={{ position: 'relative', width: isMobile ? '100%' : '200px', minWidth: isMobile ? '100%' : '180px', zIndex: 999999 }} data-dropdown-container>
            <button
              ref={setStatusButtonRef}
              onClick={() => {
                console.log('Status dropdown clicked, current state:', isStatusDropdownOpen);
                console.log('Status button ref:', statusButtonRef);
                setIsStatusDropdownOpen(!isStatusDropdownOpen);
              }}
              style={{
                width: '100%',
                padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
                border: '2px solid rgba(16, 185, 129, 0.3)',
                borderRadius: isMobile ? '12px' : '20px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(255, 255, 255, 0.95) 100%)',
                color: '#1f2937',
                fontSize: isMobile ? '0.875rem' : '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.15)',
                backdropFilter: 'blur(10px)',
                outline: 'none',
                minHeight: isMobile ? '44px' : '48px',
                borderColor: 'rgba(16, 185, 129, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: statusFilter === 'all' ? '#6b7280' :
                                   statusFilter === 'in-stock' ? '#10b981' :
                                   statusFilter === 'low-stock' ? '#f59e0b' :
                                   statusFilter === 'out-of-stock' ? '#ef4444' :
                                   statusFilter === 'expired' ? '#dc2626' : '#f97316'
                }}></div>
                <span>
                  {statusFilter === 'all' ? 'All Status' :
                   statusFilter === 'in-stock' ? 'In Stock' :
                   statusFilter === 'low-stock' ? 'Low Stock' :
                   statusFilter === 'out-of-stock' ? 'Out of Stock' :
                   statusFilter === 'expired' ? 'Expired' : 'Expiring Soon'}
                </span>
              </div>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ 
                  color: '#6b7280',
                  transform: isStatusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <polyline points="6,9 12,15 18,9"/>
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isStatusDropdownOpen && (
              <div 
                className="status-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #10b981',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                  zIndex: 999999,
                  padding: '0.125rem 0',
                  marginTop: '4px',
                  maxHeight: '96px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#10b981 #f1f5f9'
                }}
              >
                {[
                  { value: 'all', label: 'All Status', color: '#6b7280' },
                  { value: 'in-stock', label: 'In Stock', color: '#10b981' },
                  { value: 'low-stock', label: 'Low Stock', color: '#f59e0b' },
                  { value: 'out-of-stock', label: 'Out of Stock', color: '#ef4444' },
                  { value: 'expired', label: 'Expired', color: '#dc2626' },
                  { value: 'expiring-soon', label: 'Expiring Soon', color: '#f97316' }
                ].map((option, index) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onStatusFilterChange(option.value);
                      setIsStatusDropdownOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.375rem 0.5rem',
                      border: 'none',
                      background: statusFilter === option.value ? 
                        `linear-gradient(135deg, ${option.color}15 0%, ${option.color}08 100%)` : 
                        'transparent',
                      color: statusFilter === option.value ? option.color : '#374151',
                      fontSize: '0.75rem',
                      fontWeight: statusFilter === option.value ? '600' : '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderBottom: index < 5 ? '1px solid rgba(16, 185, 129, 0.08)' : 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minHeight: '32px',
                      height: '32px'
                    }}
                    onMouseEnter={(e) => {
                      if (statusFilter !== option.value) {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${option.color}12 0%, ${option.color}06 100%)`;
                        e.currentTarget.style.color = option.color;
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (statusFilter !== option.value) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }
                    }}
                  >
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: option.color,
                      boxShadow: `0 0 0 2px ${option.color}20`,
                      flexShrink: 0
                    }}></div>
                    <span>{option.label}</span>
                    {statusFilter === option.value && (
                      <div style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: `${option.color}15`,
                        border: `1px solid ${option.color}30`
                      }}>
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2.5"
                          style={{ color: option.color }}
                        >
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sort By - Custom Dropdown */}
          <div style={{ position: 'relative', width: isMobile ? '100%' : '200px', minWidth: isMobile ? '100%' : '180px', zIndex: 99999 }} data-dropdown-container>
            <button
              ref={setSortButtonRef}
              onClick={() => {
                console.log('Sort dropdown clicked, current state:', isSortDropdownOpen);
                console.log('Sort button ref:', sortButtonRef);
                setIsSortDropdownOpen(!isSortDropdownOpen);
              }}
              style={{
                width: '100%',
                padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
                border: '2px solid rgba(59, 130, 246, 0.3)',
                borderRadius: isMobile ? '12px' : '20px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(255, 255, 255, 0.95) 100%)',
                color: '#1f2937',
                fontSize: isMobile ? '0.875rem' : '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.15)',
                backdropFilter: 'blur(10px)',
                outline: 'none',
                minHeight: isMobile ? '44px' : '48px',
                borderColor: 'rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{ color: '#3b82f6' }}
                >
                  <path d="M3 6h18M3 12h18M3 18h18"/>
                </svg>
                <span>
                  {sortBy === 'name' ? 'Sort by Name' :
                   sortBy === 'quantity' ? 'Sort by Quantity' :
                   sortBy === 'unit_cost' ? 'Sort by Unit Cost' :
                   sortBy === 'selling_price' ? 'Sort by Selling Price' :
                   sortBy === 'mrp' ? 'Sort by MRP' :
                   sortBy === 'expiry' ? 'Sort by Expiry' : 'Sort by Status'}
                </span>
              </div>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ 
                  color: '#6b7280',
                  transform: isSortDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <polyline points="6,9 12,15 18,9"/>
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isSortDropdownOpen && (
              <div 
                className="sort-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #3b82f6',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                  zIndex: 999999,
                  padding: '0.125rem 0',
                  marginTop: '4px',
                  maxHeight: '96px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#3b82f6 #f1f5f9'
                }}
              >
                {[
                  { value: 'name', label: 'Sort by Name', icon: '•' },
                  { value: 'quantity', label: 'Sort by Quantity', icon: '•' },
                  { value: 'unit_cost', label: 'Sort by Unit Cost', icon: '•' },
                  { value: 'selling_price', label: 'Sort by Selling Price', icon: '•' },
                  { value: 'mrp', label: 'Sort by MRP', icon: '•' },
                  { value: 'expiry', label: 'Sort by Expiry', icon: '•' },
                  { value: 'status', label: 'Sort by Status', icon: '•' }
                ].map((option, index) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onSortByChange(option.value);
                      setIsSortDropdownOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.375rem 0.5rem',
                      border: 'none',
                      background: sortBy === option.value ? 
                        'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)' : 
                        'transparent',
                      color: sortBy === option.value ? '#3b82f6' : '#374151',
                      fontSize: '0.75rem',
                      fontWeight: sortBy === option.value ? '600' : '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderBottom: index < 6 ? '1px solid rgba(59, 130, 246, 0.08)' : 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minHeight: '32px',
                      height: '32px'
                    }}
                    onMouseEnter={(e) => {
                      if (sortBy !== option.value) {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)';
                        e.currentTarget.style.color = '#3b82f6';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sortBy !== option.value) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>{option.icon}</span>
                    <span>{option.label}</span>
                    {sortBy === option.value && (
                      <div style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(59, 130, 246, 0.15)',
                        border: '1px solid rgba(59, 130, 246, 0.30)'
                      }}>
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2.5"
                          style={{ color: '#3b82f6' }}
                        >
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? '0.75rem' : '1rem'
      }}>
        <div style={{
          padding: isMobile ? '0.625rem 0.75rem' : '0.75rem 1rem',
          background: 'rgba(34, 197, 94, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: '700', color: '#22c55e' }}>
            {medications.filter(m => getStatusText(m) === 'in-stock').length}
          </div>
          <div style={{ fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#1B5E20', fontWeight: '600' }}>In Stock</div>
        </div>
        <div style={{
          padding: isMobile ? '0.625rem 0.75rem' : '0.75rem 1rem',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: '700', color: '#f59e0b' }}>
            {medications.filter(m => getStatusText(m) === 'low-stock').length}
          </div>
          <div style={{ fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#92400e', fontWeight: '600' }}>Low Stock</div>
        </div>
        <div style={{
          padding: isMobile ? '0.625rem 0.75rem' : '0.75rem 1rem',
          background: 'rgba(249, 115, 22, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(249, 115, 22, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: '700', color: '#f97316' }}>
            {medications.filter(m => getStatusText(m) === 'expiring-soon').length}
          </div>
          <div style={{ fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#ea580c', fontWeight: '600' }}>Expiring Soon</div>
        </div>
        <div style={{
          padding: isMobile ? '0.625rem 0.75rem' : '0.75rem 1rem',
          background: 'rgba(220, 38, 38, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(220, 38, 38, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: '700', color: '#dc2626' }}>
            {medications.filter(m => getStatusText(m) === 'expired').length}
          </div>
          <div style={{ fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#b91c1c', fontWeight: '600' }}>Expired</div>
        </div>
      </div>

      <style>{`
        /* Force dropdown to appear above everything */
        .status-dropdown {
          position: absolute !important;
          z-index: 999999 !important;
          background: white !important;
          border: 1px solid #10b981 !important;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2) !important;
        }
        
        /* Custom scrollbar for dropdown */
        .status-dropdown::-webkit-scrollbar {
          width: 6px;
        }
        
        .status-dropdown::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        
        .status-dropdown::-webkit-scrollbar-thumb {
          background: #10b981;
          border-radius: 3px;
        }
        
        .status-dropdown::-webkit-scrollbar-thumb:hover {
          background: #059669;
        }
        
        /* Custom scrollbar for sort dropdown */
        .sort-dropdown::-webkit-scrollbar {
          width: 6px;
        }
        
        .sort-dropdown::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        
        .sort-dropdown::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 3px;
        }
        
        .sort-dropdown::-webkit-scrollbar-thumb:hover {
          background: #2563eb;
        }
      `}</style>
    </div>
  );
};

export default InventoryStats;

